import React, { useState } from 'react';
import { CheckCircle, ArrowRight } from 'lucide-react';

const ContactSection: React.FC = () => {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    contactVia: '',
    investmentAmount: '',
    message: '',
  });
  
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch('api/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formState),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      setSubmitted(true);
    } catch (err) {
      setError('Failed to submit form. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-navy-950">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto bg-navy-900 rounded-2xl overflow-hidden shadow-xl border border-gray-800">
          <div className="grid grid-cols-1 md:grid-cols-2">
            <div className="p-8 md:p-12 bg-gradient-to-br from-navy-800 to-navy-900">
              <h2 className="text-3xl font-bold text-white mb-6">Take the Next Step</h2>
              <p className="text-gray-300 mb-8">
                Ready for steady, secure earnings? Leave your contact details, and one of our specialists will reach out within one business day.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="mr-4 text-gold-500 mt-1">
                    <CheckCircle size={20} />
                  </div>
                  <p className="text-gray-300">Clarify returns, risks, and safeguards.</p>
                </div>
                <div className="flex items-start">
                  <div className="mr-4 text-gold-500 mt-1">
                    <CheckCircle size={20} />
                  </div>
                  <p className="text-gray-300">Draft a personalized investment plan.</p>
                </div>
                <div className="flex items-start">
                  <div className="mr-4 text-gold-500 mt-1">
                    <CheckCircle size={20} />
                  </div>
                  <p className="text-gray-300">Arrange the easiest on-ramp and payout method for you.</p>
                </div>
              </div>
              
              <div className="mt-12 p-6 bg-navy-800 rounded-xl border border-gray-700">
                <h3 className="text-xl font-semibold text-white mb-4">Have questions?</h3>
                <p className="text-gray-300 mb-4">
                  Our team is ready to answer any questions about our investment approach, security measures, or performance history.
                </p>
                <div className="flex items-center text-gold-500">
                  <a href="#" className="flex items-center hover:text-gold-400 transition-colors">
                    Contact us <ArrowRight className="ml-2" size={18} />
                  </a>
                </div>
              </div>
            </div>
            
            <div className="p-8 md:p-12 bg-navy-800">
              {!submitted ? (
                <form onSubmit={handleSubmit}>
                  <h3 className="text-2xl font-semibold text-white mb-6">Let's Connect</h3>
                  
                  {error && (
                    <div className="mb-4 p-3 bg-red-500/10 border border-red-500/50 rounded-lg text-red-500">
                      {error}
                    </div>
                  )}
                  
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1">Name*</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formState.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-navy-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-500 focus:border-transparent text-white"
                        placeholder="Your name"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">Email Address*</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formState.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-navy-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-500 focus:border-transparent text-white"
                        placeholder="your@email.com"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="contactVia" className="block text-sm font-medium text-gray-300 mb-1">Contact via</label>
                      <input
                        type="text"
                        id="contactVia"
                        name="contactVia"
                        value={formState.contactVia}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-navy-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-500 focus:border-transparent text-white"
                        placeholder="Telegram, WhatsApp, or phone number"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="investmentAmount" className="block text-sm font-medium text-gray-300 mb-1">Estimated Investment Amount</label>
                      <select
                        id="investmentAmount"
                        name="investmentAmount"
                        value={formState.investmentAmount}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-navy-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-500 focus:border-transparent text-white"
                      >
                        <option value="">Select amount</option>
                        <option value="50-100k">$50,000 - $100,000</option>
                        <option value="100-250k">$100,000 - $250,000</option>
                        <option value="250-500k">$250,000 - $500,000</option>
                        <option value="500k-1m">$500,000 - $1,000,000</option>
                        <option value="1m+">$1,000,000+</option>
                      </select>
                    </div>
                    
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-1">Message (Optional)</label>
                      <textarea
                        id="message"
                        name="message"
                        value={formState.message}
                        onChange={handleChange}
                        rows={4}
                        className="w-full px-4 py-3 bg-navy-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-500 focus:border-transparent text-white resize-none"
                        placeholder="Any specific questions or requirements..."
                      ></textarea>
                    </div>
                    
                    <div className="pt-2">
                      <button 
                        type="submit" 
                        disabled={isSubmitting}
                        className="w-full bg-gold-500 hover:bg-gold-600 text-navy-900 font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center group disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? 'Submitting...' : "Let's Connect"}
                        <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
                      </button>
                    </div>
                  </div>
                </form>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-center">
                  <div className="text-green-400 mb-6">
                    <CheckCircle size={64} />
                  </div>
                  <h3 className="text-2xl font-semibold text-white mb-4">Thank You!</h3>
                  <p className="text-gray-300 mb-8">
                    Your request has been received. One of our investment specialists will contact you within one business day.
                  </p>
                  <button 
                    onClick={() => setSubmitted(false)}
                    className="px-6 py-2 border border-gray-500 text-gray-300 rounded-lg hover:bg-navy-700 transition-colors"
                  >
                    Send Another Request
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;